"use client";

import { useState } from "react";
import { authClient } from "@/lib/auth-client";
import Link from "next/link";
import { Beef } from "lucide-react";

export default function SignInPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const { error: signInError } = await authClient.signIn.email({
      email,
      password,
    });

    if (signInError) {
      setError(signInError.message || "Sign in failed");
      setLoading(false);
      return;
    }

    try {
      await fetch("/api/cart/merge", { method: "POST" });
    } catch {
      // Cart merge is best-effort — don't block sign-in if it fails
    }

    window.location.href = "/account";
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[var(--color-background)] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <Beef className="w-10 h-10 text-[var(--color-accent)] mx-auto mb-3" strokeWidth={1.5} />
          <h1 className="text-xl font-heading font-bold text-[var(--color-foreground)]">Welcome Back</h1>
          <p className="text-sm text-[var(--color-muted)] mt-1">Sign in to your account</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-[var(--color-border)] rounded-xl p-6 space-y-4">
          {error && (
            <div className="text-sm text-[var(--color-destructive)] bg-red-50 border border-red-200 rounded-lg p-2">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border border-[var(--color-border)] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/30 focus:border-[var(--color-accent)]"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border border-[var(--color-border)] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/30 focus:border-[var(--color-accent)]"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full px-4 py-2.5 bg-[var(--color-accent)] text-white rounded-lg font-medium hover:bg-[var(--color-accent-light)] transition-colors disabled:opacity-50"
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>

          <div className="flex flex-col gap-2 text-center text-sm">
            <Link
              href="/forgot-password"
              className="text-[var(--color-muted)] hover:text-[var(--color-accent)] transition-colors"
            >
              Forgot password?
            </Link>
            <Link
              href="/sign-up"
              className="text-[var(--color-muted)] hover:text-[var(--color-accent)] transition-colors"
            >
              Don&apos;t have an account? Sign up
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
